// Shared screen modal (M57.4, ARCH §30.6, COW-9). Share / Watch / Stop, the
// viewer list (name + connecting|live|relayed|failed), and the LIVE badge.
// Client-only chrome — the sim never learns a share exists (D-TF-180).
import { h } from '../h';
import type { ViewerState } from '../../net/webrtc';

export interface ScreenModalState {
  /** Whether a share is currently live at this cell, and by whom. */
  live: boolean;
  /** True when THIS client is the one sharing. */
  isSharer: boolean;
  /** True when THIS client is currently watching. */
  isWatching: boolean;
  /** Sharer-side viewer list (empty unless `isSharer`). */
  viewers: Array<{ name: string; state: ViewerState }>;
}

/** Open the modal; `onShare`/`onWatch`/`onStop` apply the chosen action.
 *  Returns a close handle. */
export function openScreenModal(
  state: ScreenModalState,
  onShare: () => void, onWatch: () => void, onStop: () => void,
): () => void {
  const badge = state.live ? h('div', { class: 'bb-screen-live-badge' }, 'LIVE') : h('div', {});
  const rows: HTMLElement[] = [];
  if (state.isSharer) {
    rows.push(h('div', { class: 'bb-screen-viewers-title' }, 'Viewers'));
    for (const v of state.viewers) rows.push(h('div', { class: 'bb-screen-viewer-row' }, `${v.name}: ${v.state}`));
    if (state.viewers.length === 0) rows.push(h('div', {}, '(nobody watching yet)'));
  }
  const share = h('button', { class: 'bb-btn' }, 'Share');
  const watch = h('button', { class: 'bb-btn' }, 'Watch');
  const stop = h('button', { class: 'bb-btn' }, 'Stop');
  const close_ = h('button', { class: 'bb-btn' }, 'Close');
  share.style.display = !state.live ? 'inline-block' : 'none';
  watch.style.display = state.live && !state.isSharer && !state.isWatching ? 'inline-block' : 'none';
  stop.style.display = state.isSharer || state.isWatching ? 'inline-block' : 'none';
  const panel = h('div', { class: 'bb-screen-editor' },
    h('div', { class: 'bb-screen-title' }, 'Shared Screen'), badge, ...rows, share, watch, stop, close_);
  panel.style.cssText = 'background:#2b2b2b;color:#fff;padding:16px;border:2px solid #000;text-align:center;min-width:220px;';
  const overlay = h('div', { class: 'bb-modal bb-screen-modal' }, panel);
  overlay.style.cssText = 'position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;background:#0006;';
  document.body.append(overlay);

  const closeFn = (): void => { overlay.remove(); };
  share.addEventListener('click', () => { onShare(); closeFn(); });
  watch.addEventListener('click', () => { onWatch(); closeFn(); });
  stop.addEventListener('click', () => { onStop(); closeFn(); });
  close_.addEventListener('click', closeFn);
  return closeFn;
}
